/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useTheme } from "@mui/material/styles";
import Swal from "sweetalert2";

import LinearProgress from "@mui/material/LinearProgress";

import StatTile from "shell/StatTile";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import MemberLedgerBook from "components/MemberLedgerBook";
import useMemberLedgerData from "layouts/tables/data/memberLedgerData";
import HeadDashboard from "../HeadDashboard";

const LEDGER_YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

export default function MemberDashboard() {
  const theme = useTheme();
  const { user, church } = useAuth();
  const token = localStorage.getItem("authToken");
  const memberId = user?.member_id;

  const [tab, setTab] = useState(0);
  const [tithes, setTithes] = useState([]);
  const [welfares, setWelfares] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [dues, setDues] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [memberGroups, setMemberGroups] = useState({ department: null, ministry: null });
  const [campaigns, setCampaigns] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const titheLedger = useMemberLedgerData("tithe", memberId);
  const welfareLedger = useMemberLedgerData("welfare", memberId);

  // Edit profile dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [editPhoto, setEditPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    // Pre-fill with current member data fetched in loadData
    setEditData({
      first_name: "",
      surname: "",
      middle_name: "",
      phone: "",
      email: "",
      address: "",
      occupation: "",
      marital_status: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
    });
    setEditPhoto(null);
    setEditOpen(true);
    // Load fresh member data
    fetch(`${API_BASE}/members/${memberId}`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const m = d.member || d;
        setEditData({
          first_name: m.first_name || "",
          surname: m.surname || "",
          middle_name: m.middle_name || "",
          phone: m.phone || "",
          email: m.email || "",
          address: m.address || "",
          occupation: m.occupation || "",
          marital_status: m.marital_status || "",
          emergency_contact_name: m.emergency_contact_name || "",
          emergency_contact_phone: m.emergency_contact_phone || "",
        });
      })
      .catch(() => {});
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const form = new FormData();
      Object.entries(editData).forEach(([k, v]) => {
        if (v !== "") form.append(k, v);
      });
      form.append("_method", "PUT");
      if (editPhoto) form.append("photo", editPhoto);
      const res = await fetch(`${API_BASE}/members/me`, {
        method: "POST",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        body: form,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || "Update failed");
      Swal.fire("Saved!", "Your profile has been updated.", "success");
      setEditOpen(false);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // Pay dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payType, setPayType] = useState("tithe");
  const [payProjectId, setPayProjectId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payEmail, setPayEmail] = useState(
    user?.email && !/\.(local|test|example|invalid|internal)$/i.test(user.email) ? user.email : ""
  );
  const [paying, setPaying] = useState(false);

  // Surface Paystack redirect result via a toast (set by the API after callback)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (!payment) return;
    if (payment === "success")
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Payment received. God bless you!",
        showConfirmButton: false,
        timer: 3500,
      });
    else if (payment === "cancelled")
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Payment cancelled",
        showConfirmButton: false,
        timer: 3000,
      });
    else if (payment === "unknown")
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "We couldn't find that payment reference",
        showConfirmButton: false,
        timer: 3500,
      });
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!memberId || !token) {
      setLoading(false);
      return;
    }
    const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };
    const churchId = church?.id;
    Promise.all([
      fetch(`${API_BASE}/report/member/${memberId}/tithe`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE}/report/member/${memberId}/welfare`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE}/report/member/${memberId}/dues`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE}/report/member/${memberId}/contribution`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/members/${memberId}`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE}/attendance/member/${memberId}`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE}/projects/mine`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE}/events/mine`, { headers }).then((r) => r.json()),
    ])
      .then(([t, w, d, c, memberRecord, a, proj, ev]) => {
        setTithes(Array.isArray(t) ? t : t?.data || []);
        setWelfares(Array.isArray(w) ? w : w?.data || []);
        setDues(Array.isArray(d) ? d : d?.data || []);
        setContributions(Array.isArray(c) ? c : c?.data || []);
        setAttendance(Array.isArray(a) ? a : a?.data || []);
        setCampaigns(Array.isArray(proj?.data) ? proj.data : []);
        const allEvents = Array.isArray(ev?.data) ? ev.data : [];
        setUpcomingEvents(
          allEvents
            .filter((e) => !e.is_done)
            .sort((x, y) => new Date(x.start_at) - new Date(y.start_at))
        );
        const record = memberRecord?.data || memberRecord;
        setMemberGroups({
          department: record?.department || null,
          ministry: record?.ministry || null,
        });
      })
      .catch((e) => console.error("MemberDashboard fetch:", e))
      .finally(() => setLoading(false));
  }, [memberId, token, church?.id]);

  const titheTotal = useMemo(() => tithes.reduce((s, p) => s + Number(p.amount || 0), 0), [tithes]);
  const welfareTotal = useMemo(
    () => welfares.reduce((s, p) => s + Number(p.amount || 0), 0),
    [welfares]
  );
  const duesTotal = useMemo(() => dues.reduce((s, p) => s + Number(p.amount || 0), 0), [dues]);
  const contributionTotal = useMemo(
    () => contributions.reduce((s, p) => s + Number(p.amount || 0), 0),
    [contributions]
  );

  const isDepartmentHead =
    !!memberGroups.department &&
    Number(memberGroups.department.head_member_id) === Number(memberId);
  const isMinistryHead =
    !!memberGroups.ministry && Number(memberGroups.ministry.head_member_id) === Number(memberId);

  const handlePay = async () => {
    if (!payAmount || Number(payAmount) <= 0)
      return Swal.fire({ title: "Enter amount", icon: "warning" });
    if (!payEmail) return Swal.fire({ title: "Email is required", icon: "warning" });
    setPaying(true);
    try {
      const res = await fetch(`${API_BASE}/payments/paystack/initialize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          member_id: memberId,
          email: payEmail,
          amount: Number(payAmount),
          type: payType,
          project_id: payProjectId || null,
          church_id: church?.id,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Could not start payment");
      const url = json?.data?.authorization_url;
      if (!url) throw new Error("No authorization URL returned");
      window.location.href = url;
    } catch (e) {
      Swal.fire({ title: "Payment failed", text: e.message, icon: "error" });
    } finally {
      setPaying(false);
    }
  };

  const handleContributeToCampaign = (campaign) => {
    setPayType("contribution");
    setPayProjectId(campaign.id);
    setPayAmount("");
    setPayOpen(true);
  };

  if (!memberId) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography color="text.secondary">
          Your member profile isn&apos;t linked yet. Please contact the church office.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hero */}
      <Card
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3.5 },
          background: `linear-gradient(135deg, ${theme.palette.teal[600]}, ${theme.palette.teal[400]})`,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -50,
            top: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.10)",
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="overline" sx={{ color: "white", opacity: 0.9 }}>
              Welcome back
            </Typography>
            <Typography variant="h2" sx={{ color: "white" }}>
              {firstName(user?.name)}
            </Typography>
            <Typography variant="body2" sx={{ color: "white", opacity: 0.9, mt: 0.25 }}>
              {church?.name || "Your church"} · Member dashboard
            </Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Button
              variant="outlined"
              onClick={openEdit}
              startIcon={<Icon>edit</Icon>}
              sx={{
                borderColor: "rgba(255,255,255,0.6)",
                color: "white",
                fontWeight: 700,
                "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              Edit Profile
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setPayType("tithe");
                setPayProjectId(null);
                setPayOpen(true);
              }}
              startIcon={<Icon>credit_card</Icon>}
              sx={{
                bgcolor: "white",
                color: theme.palette.teal[700],
                fontWeight: 700,
                "&:hover": { bgcolor: theme.palette.teal[50] },
              }}
            >
              Pay Now
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="payments"
            label="Total Tithe"
            value={`GHS ${titheTotal.toFixed(2)}`}
            sub={`${tithes.length} payment${tithes.length === 1 ? "" : "s"}`}
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="health_and_safety"
            label="Total Welfare"
            value={`GHS ${welfareTotal.toFixed(2)}`}
            sub={`${welfares.length} payment${welfares.length === 1 ? "" : "s"}`}
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="how_to_reg"
            label="Attendance"
            value={attendance.length}
            sub="services attended"
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="badge"
            label="Member ID"
            value={user?.member?.memberID || "—"}
            sub={user?.email || ""}
            tone="purple"
          />
        </Grid>
      </Grid>

      {/* Upcoming events / programs */}
      {!loading && upcomingEvents.length > 0 && (
        <Card sx={{ mb: 3, p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Upcoming Events &amp; Programs
          </Typography>
          <Grid container spacing={2}>
            {upcomingEvents.slice(0, 4).map((e) => {
              const scope = e.department_id
                ? { label: e.department?.name || "Department", color: "#0288D1" }
                : e.ministry_id
                ? { label: e.ministry?.name || "Ministry", color: "#ED6C02" }
                : { label: "General", color: theme.palette.teal[600] };
              return (
                <Grid item xs={12} sm={6} md={3} key={e.id}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderLeft: `4px solid ${scope.color}`,
                      height: "100%",
                    }}
                  >
                    <Chip
                      size="small"
                      label={scope.label}
                      sx={{ bgcolor: `${scope.color}1A`, color: scope.color, mb: 0.75, height: 20 }}
                    />
                    <Typography variant="button" fontWeight={700} display="block">
                      {e.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(e.start_at).toLocaleString(undefined, {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                      {e.location ? ` · ${e.location}` : ""}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      )}

      {/* History tabs */}
      <Card sx={{ overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: { xs: 0, sm: 2 },
            borderBottom: `1px solid ${theme.palette.divider}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 48,
              minWidth: { xs: 80, sm: 120 },
            },
          }}
        >
          <Tab icon={<Icon fontSize="small">payments</Icon>} iconPosition="start" label="Tithe" />
          <Tab
            icon={<Icon fontSize="small">health_and_safety</Icon>}
            iconPosition="start"
            label="Welfare"
          />
          <Tab
            icon={<Icon fontSize="small">how_to_reg</Icon>}
            iconPosition="start"
            label="Attendance"
          />
          <Tab icon={<Icon fontSize="small">groups</Icon>} iconPosition="start" label="Groups" />
          <Tab
            icon={<Icon fontSize="small">volunteer_activism</Icon>}
            iconPosition="start"
            label="Campaigns"
          />
          {isDepartmentHead && (
            <Tab
              icon={<Icon fontSize="small">military_tech</Icon>}
              iconPosition="start"
              label="Dept. Lead"
            />
          )}
          {isMinistryHead && (
            <Tab
              icon={<Icon fontSize="small">military_tech</Icon>}
              iconPosition="start"
              label="Ministry Lead"
            />
          )}
        </Tabs>
        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            (() => {
              // Fixed tabs: 0 tithe, 1 welfare, 2 attendance, 3 my groups, 4 campaigns,
              // then optional leadership tabs in the same order they're rendered above.
              const leadershipTabs = [
                ...(isDepartmentHead ? ["department"] : []),
                ...(isMinistryHead ? ["ministry"] : []),
              ];
              if (tab === 0)
                return (
                  <MemberBookSection
                    ledger={titheLedger}
                    title="My Tithe Book"
                    rows={tithes}
                    emptyText="No tithe payments yet."
                  />
                );
              if (tab === 1)
                return (
                  <MemberBookSection
                    ledger={welfareLedger}
                    title="My Welfare Book"
                    rows={welfares}
                    emptyText="No welfare payments yet."
                  />
                );
              if (tab === 2) return <AttendanceTable rows={attendance} />;
              if (tab === 3)
                return (
                  <MyGroupsPanel
                    department={memberGroups.department}
                    ministry={memberGroups.ministry}
                    dues={dues}
                    duesTotal={duesTotal}
                    contributions={contributions}
                    contributionTotal={contributionTotal}
                    isDepartmentHead={isDepartmentHead}
                    isMinistryHead={isMinistryHead}
                  />
                );
              if (tab === 4)
                return (
                  <CampaignsPanel campaigns={campaigns} onContribute={handleContributeToCampaign} />
                );
              const kind = leadershipTabs[tab - 5];
              return kind ? <HeadDashboard kind={kind} /> : null;
            })()
          )}
        </Box>
      </Card>

      {/* Pay dialog */}
      <FormDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title="Make a Payment"
        onSave={handlePay}
        saveLabel="Continue"
        loading={paying}
        maxWidth="xs"
      >
        <FormSection>
          {payProjectId ? (
            <Chip
              size="small"
              color="success"
              icon={<Icon fontSize="small">volunteer_activism</Icon>}
              label={`Contributing to: ${
                campaigns.find((c) => c.id === payProjectId)?.name || "Campaign"
              }`}
              sx={{ mb: 1 }}
            />
          ) : (
            <FormControl fullWidth margin="normal">
              <InputLabel>Type</InputLabel>
              <Select value={payType} label="Type" onChange={(e) => setPayType(e.target.value)}>
                <MenuItem value="tithe">Tithe</MenuItem>
                <MenuItem value="welfare">Welfare</MenuItem>
                <MenuItem value="offering">Offering</MenuItem>
                <MenuItem value="dues">Dues</MenuItem>
                <MenuItem value="harvest">Harvest</MenuItem>
              </Select>
            </FormControl>
          )}
          <TextField
            label="Amount (GHS)"
            type="number"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Email for receipt"
            type="email"
            value={payEmail}
            onChange={(e) => setPayEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            You will be redirected to Paystack to complete the payment.
          </Typography>
        </FormSection>
      </FormDialog>

      {/* Edit Profile dialog */}
      <FormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit My Profile"
        onSave={handleSaveProfile}
        saveLabel="Save Changes"
        loading={saving}
        maxWidth="sm"
      >
        <FormSection>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                fullWidth
                size="small"
                value={editData.first_name || ""}
                onChange={(e) => setEditData((p) => ({ ...p, first_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Surname"
                fullWidth
                size="small"
                value={editData.surname || ""}
                onChange={(e) => setEditData((p) => ({ ...p, surname: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Middle Name"
                fullWidth
                size="small"
                value={editData.middle_name || ""}
                onChange={(e) => setEditData((p) => ({ ...p, middle_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                size="small"
                value={editData.phone || ""}
                onChange={(e) => setEditData((p) => ({ ...p, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                size="small"
                value={editData.email || ""}
                onChange={(e) => setEditData((p) => ({ ...p, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Occupation"
                fullWidth
                size="small"
                value={editData.occupation || ""}
                onChange={(e) => setEditData((p) => ({ ...p, occupation: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                size="small"
                value={editData.address || ""}
                onChange={(e) => setEditData((p) => ({ ...p, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Marital Status</InputLabel>
                <Select
                  value={editData.marital_status || ""}
                  label="Marital Status"
                  onChange={(e) => setEditData((p) => ({ ...p, marital_status: e.target.value }))}
                >
                  {["Single", "Married", "Divorced", "Widowed"].map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Emergency Contact Name"
                fullWidth
                size="small"
                value={editData.emergency_contact_name || ""}
                onChange={(e) =>
                  setEditData((p) => ({ ...p, emergency_contact_name: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Emergency Contact Phone"
                fullWidth
                size="small"
                value={editData.emergency_contact_phone || ""}
                onChange={(e) =>
                  setEditData((p) => ({ ...p, emergency_contact_phone: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Profile photo (optional)
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setEditPhoto(e.target.files[0] || null)}
                style={{ fontSize: 14 }}
              />
            </Grid>
          </Grid>
        </FormSection>
      </FormDialog>
    </Box>
  );
}

// ---------- helpers ----------

function MyGroupsPanel({
  department,
  ministry,
  dues,
  duesTotal,
  contributions,
  contributionTotal,
  isDepartmentHead,
  isMinistryHead,
}) {
  if (!department && !ministry) {
    return <Empty text="You're not yet assigned to a department or ministry." />;
  }

  return (
    <Grid container spacing={2.5}>
      {department && (
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2.5, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="h6">{department.name}</Typography>
              {isDepartmentHead && (
                <Chip size="small" color="primary" label="You lead this department" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Department
            </Typography>
            {department.description && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {department.description}
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              My dues
            </Typography>
            <Typography variant="h4" sx={{ mb: 1 }}>
              GHS {duesTotal.toFixed(2)}
            </Typography>
            <DuesMiniTable rows={dues} />
          </Card>
        </Grid>
      )}

      {ministry && (
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2.5, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="h6">{ministry.name}</Typography>
              {isMinistryHead && (
                <Chip size="small" color="primary" label="You lead this ministry" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Ministry
            </Typography>
            {ministry.description && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {ministry.description}
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              My contributions
            </Typography>
            <Typography variant="h4" sx={{ mb: 1 }}>
              GHS {contributionTotal.toFixed(2)}
            </Typography>
            <DuesMiniTable rows={contributions} />
          </Card>
        </Grid>
      )}
    </Grid>
  );
}

function CampaignsPanel({ campaigns, onContribute }) {
  if (!campaigns.length) {
    return <Empty text="No active contribution campaigns right now." />;
  }
  return (
    <Grid container spacing={2.5}>
      {campaigns.map((c) => {
        const goal = Number(c.goal_amount || 0);
        const raised = Number(c.contributions_sum_amount || 0);
        const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
        const scopeLabel = c.department?.name
          ? `Department: ${c.department.name}`
          : c.ministry?.name
          ? `Ministry: ${c.ministry.name}`
          : "General (church-wide)";
        return (
          <Grid item xs={12} md={6} key={c.id}>
            <Card variant="outlined" sx={{ p: 2.5, height: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">{c.name}</Typography>
                <Chip size="small" label={scopeLabel} />
              </Box>
              {c.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {c.description}
                </Typography>
              )}
              <Box mt={2}>
                <Typography variant="h5" fontWeight={800}>
                  GHS {raised.toFixed(2)}
                </Typography>
                {goal > 0 && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {pct}% of GHS {goal.toFixed(2)} goal
                    </Typography>
                  </>
                )}
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<Icon fontSize="small">volunteer_activism</Icon>}
                sx={{ mt: 2 }}
                onClick={() => onContribute(c)}
              >
                Contribute
              </Button>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

function DuesMiniTable({ rows }) {
  if (!rows.length) return <Empty text="Nothing recorded yet." />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell>
                {p.payment_date ? new Date(p.payment_date).toLocaleDateString() : "—"}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>GHS {Number(p.amount).toFixed(2)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={p.status}
                  color={
                    p.status === "success"
                      ? "success"
                      : p.status === "pending"
                      ? "warning"
                      : "error"
                  }
                  sx={{ textTransform: "capitalize" }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function MemberBookSection({ ledger, title, rows, emptyText }) {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        <TextField
          select
          size="small"
          label="Year"
          value={ledger.year}
          onChange={(e) => ledger.handleYearChange(Number(e.target.value))}
          sx={{ width: 120 }}
        >
          {LEDGER_YEARS.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <MemberLedgerBook
        rows={ledger.rows}
        meta={ledger.meta}
        isLoading={ledger.isLoading}
        error={ledger.error}
        onMemberClick={() => {}}
      />

      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1.5 }}>
        Transaction History
      </Typography>
      <PaymentTable rows={rows} emptyText={emptyText} />
    </Box>
  );
}

function PaymentTable({ rows, emptyText }) {
  if (!rows.length) {
    return <Empty text={emptyText} />;
  }
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Method</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell>
                {p.payment_date ? new Date(p.payment_date).toLocaleDateString() : "—"}
              </TableCell>
              <TableCell sx={{ textTransform: "capitalize" }}>{p.type}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>GHS {Number(p.amount).toFixed(2)}</TableCell>
              <TableCell sx={{ textTransform: "capitalize" }}>{p.method}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{p.reference || "—"}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={p.status}
                  color={
                    p.status === "success"
                      ? "success"
                      : p.status === "pending"
                      ? "warning"
                      : "error"
                  }
                  sx={{ textTransform: "capitalize" }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function AttendanceTable({ rows }) {
  if (!rows.length) return <Empty text="No attendance records yet." />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date / time</TableCell>
            <TableCell>Method</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.id} hover>
              <TableCell>{new Date(a.checked_in_at).toLocaleString()}</TableCell>
              <TableCell sx={{ textTransform: "capitalize" }}>{a.method}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function Empty({ text }) {
  return (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <Icon sx={{ fontSize: 36, color: "text.disabled" }}>inbox</Icon>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {text}
      </Typography>
    </Box>
  );
}

function firstName(name) {
  return (name || "Member").split(" ")[0];
}
